import React from "react";
import { useTranslation } from "react-i18next";
import FileUpload from "../../FileUpload";
import { API_ENDPOINTS } from "../../../config/constants";
import { formatDate } from "../utils/formatDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faExclamationTriangle,
  faPlay,
  faCamera,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import UserAttributionPill from "./UserAttributionPill";

const BrokenMediaFallback = ({ isVideo }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg dark:bg-gray-800">
      <div className="text-center p-4">
        <svg
          className="w-12 h-12 mx-auto text-gray-400 mb-2 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={
              isVideo
                ? "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                : "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            }
          />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("vehicleProfile.images.loadError")}
        </p>
      </div>
    </div>
  );
};

const VehicleImages = ({ vehicle, user, imageState, onSubmitImage }) => {
  const { t } = useTranslation();
  const {
    showImageForm,
    setShowImageForm,
    currentImageIndex,
    approvedImages,
    setApprovedImages,
    imageUrl,
    setImageUrl,
    imageMessage,
    setImageMessage,
    mediaErrors,
    handleImageNav,
    handleThumbnailClick,
    handleMediaError,
    handleMediaLoad,
    handleMediaLoadStart,
    isVideoUrl,
    setCurrentImageIndex,
    hasG80Badge,
    hasG80Kit,
    handleSetPrimary,
  } = imageState;

  const handleReportImage = (imageId) => {
    const reason = window.prompt(t("vehicleProfile.images.reportPrompt"));
    const imageToReport = approvedImages[currentImageIndex];

    fetch(API_ENDPOINTS.REPORT_IMAGE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        vehicleId: vehicle._id,
        imageId: imageToReport._id,
        imageUrl: imageToReport.url,
        reason,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message);
        }
        return res.json();
      })
      .then((data) => {
        let message = t("vehicleProfile.images.reportSuccess", {
          count: data.reportCount,
        });
        if (data.isHidden) {
          message += " " + t("vehicleProfile.images.reportHidden");
          setApprovedImages((prev) =>
            prev.filter((img) => img._id !== imageToReport._id)
          );
          if (currentImageIndex >= approvedImages.length - 1) {
            setCurrentImageIndex(Math.max(0, approvedImages.length - 2));
          }
        }
        window.alert(message);
      })
      .catch((err) => {
        console.error("Error reporting image", err);
        if (err.message === "You have already reported this image") {
          window.alert(t("vehicleProfile.images.alreadyReported"));
        } else {
          window.alert(t("vehicleProfile.images.reportError"));
        }
      });
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm(t("vehicleProfile.images.deleteConfirm"))) {
      return;
    }

    try {
      const res = await fetch(
        API_ENDPOINTS.DELETE_VEHICLE_IMAGE(vehicle._id, imageId),
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(
          error.message || t("vehicleProfile.images.deleteError")
        );
      }

      const updatedVehicle = await res.json();
      setApprovedImages(updatedVehicle.images.filter((img) => img.approvedAt));
      if (currentImageIndex >= approvedImages.length - 1) {
        setCurrentImageIndex(Math.max(0, approvedImages.length - 2));
      }
      setImageMessage(t("vehicleProfile.images.deleteSuccess"));
    } catch (err) {
      console.error("Error deleting image:", err);
      setImageMessage(err.message || t("vehicleProfile.images.deleteError"));
    }
  };

  const isCurrentImageOwner =
    user && approvedImages[currentImageIndex]?.submittedBy?._id === user._id;

  const supportsWebP = true; // Modern browsers support WebP

  const renderMedia = (image, isFullSize = true) => {
    if (isVideoUrl(image.url)) {
      return isFullSize ? (
        <video
          controls
          className="max-h-[520px] w-full object-cover"
          onLoadStart={() => handleMediaLoadStart(image._id)}
          onError={() => handleMediaError(image._id)}
          onLoadedData={() => handleMediaLoad(image._id)}
        >
          <source src={image.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : (
        // Thumbnail version for video
        <div className="relative h-20 w-20">
          <video
            className="h-full w-full object-cover rounded-lg"
            onLoadStart={() => handleMediaLoadStart(image._id)}
            onError={() => handleMediaError(image._id)}
            onLoadedData={() => handleMediaLoad(image._id)}
          >
            <source src={image.url} type="video/mp4" />
          </video>
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
            <FontAwesomeIcon icon={faPlay} className="text-white text-lg" />
          </div>
        </div>
      );
    }

    if (isFullSize) {
      return (
        <picture>
          {supportsWebP && image.variants?.webp?.large && (
            <source srcSet={`${image.variants.webp.large}`} type="image/webp" />
          )}
          <img
            src={image.variants?.large || image.url}
            alt=""
            className="w-full object-cover cursor-zoom-in h-full"
            onLoadStart={() => handleMediaLoadStart(image._id)}
            onError={() => handleMediaError(image._id)}
            onLoad={() => handleMediaLoad(image._id)}
            onClick={() => window.open(image.url, "_blank")}
          />
        </picture>
      );
    } else {
      // Thumbnail version
      return (
        <div className="relative h-full w-full">
          <picture className="h-full w-full block">
            {supportsWebP && image.variants?.webp?.thumbnail && (
              <source
                srcSet={`${image.variants.webp.thumbnail}`}
                type="image/webp"
              />
            )}
            <img
              src={image.variants?.thumbnail || image.url}
              alt=""
              className="h-full w-full object-cover"
              onLoadStart={() => handleMediaLoadStart(image._id)}
              onError={() => handleMediaError(image._id)}
              onLoad={() => handleMediaLoad(image._id)}
            />
          </picture>
          {image.isPrimary && (
            <div className="absolute top-1 right-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs px-1.5 py-0.5 rounded-full dark:from-primary-600 dark:to-primary-700">
              Primary
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold">
            {t("vehicleProfile.images.title")}
          </h3>
        </div>
        {approvedImages.length > 0 &&
          currentImageIndex < approvedImages.length && (
            <UserAttributionPill
              username={approvedImages[currentImageIndex].submittedBy?.username}
              profilePic={
                approvedImages[currentImageIndex].submittedBy
                  ?.profilePicVariants?.small ||
                approvedImages[currentImageIndex].submittedBy?.profilePic
              }
              date={approvedImages[currentImageIndex].submittedAt}
              type="added"
            />
          )}
      </div>

      {approvedImages.length > 0 ? (
        <div className="space-y-3">
          {/* Main Image Display */}
          <div className="relative w-full aspect-[16/9] max-h-[520px] max-w-[2560px] mx-auto rounded-md">
            {/* G80 Badge */}
            {(hasG80Badge || hasG80Kit) && (
              <span
                className={`absolute top-2 left-2 text-center text-xs font-medium py-0.5 px-2 rounded z-10 ${
                  hasG80Badge
                    ? "bg-green-100/90 text-green-800"
                    : "bg-yellow-100/90 text-yellow-800"
                }`}
              >
                {hasG80Badge ? "G80" : "G80 Kit"}
              </span>
            )}
            {mediaErrors[approvedImages[currentImageIndex]._id] ? (
              <div className="absolute inset-0">
                <div className="flex items-center justify-center h-[400px] bg-gray-100 text-gray-500">
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="text-4xl mb-2"
                    />
                    <p>Failed to load media</p>
                  </div>
                </div>
              </div>
            ) : (
              renderMedia(approvedImages[currentImageIndex], true)
            )}

            <div className="absolute top-2 right-2 flex gap-2">
              {isCurrentImageOwner && (
                <button
                  onClick={() =>
                    handleDeleteImage(approvedImages[currentImageIndex]._id)
                  }
                  className="bg-red-600 text-white px-2 py-0.5 rounded-full text-xs hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              )}
              <button
                onClick={() =>
                  handleReportImage(approvedImages[currentImageIndex]._id)
                }
                className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs hover:bg-red-600 transition-colors"
              >
                Report
              </button>
            </div>

            {approvedImages.length > 1 && (
              <>
                <button
                  onClick={() => handleImageNav("prev")}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  onClick={() => handleImageNav("next")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {approvedImages.length > 1 && (
            <div className="flex flex-wrap gap-2 overflow-x-auto py-1 px-2">
              {approvedImages.map((image, idx) => (
                <button
                  key={image._id}
                  onClick={() => handleThumbnailClick(idx)}
                  className={`relative flex-shrink-0 w-24 h-14 rounded-lg overflow-hidden ${
                    idx === currentImageIndex
                      ? "ring-2 ring-primary-500 dark:ring-primary-400"
                      : ""
                  }`}
                >
                  <div className="w-full h-full">
                    {isVideoUrl(image.url) ? (
                      <div className="relative w-full h-full">
                        {!mediaErrors[image._id] ? (
                          <>
                            <video
                              src={image.url}
                              className="w-full h-full object-cover bg-black"
                              onError={() => handleMediaError(image._id)}
                              onLoadedData={() => handleMediaLoad(image._id)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                              </svg>
                            </div>
                          </>
                        ) : (
                          <BrokenMediaFallback isVideo={true} />
                        )}
                      </div>
                    ) : (
                      <>
                        {!mediaErrors[image._id] ? (
                          <picture className="h-full w-full block">
                            {supportsWebP &&
                              image.variants?.webp?.thumbnail && (
                                <source
                                  srcSet={`${image.variants.webp.thumbnail}`}
                                  type="image/webp"
                                />
                              )}
                            <img
                              src={image.variants?.thumbnail || image.url}
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={() => handleMediaError(image._id)}
                              onLoad={() => handleMediaLoad(image._id)}
                            />
                          </picture>
                        ) : (
                          <BrokenMediaFallback isVideo={false} />
                        )}
                      </>
                    )}
                  </div>
                  {image.isPrimary ? (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-[10px] py-0.5 text-center dark:from-primary-600 dark:to-primary-700">
                      Primary
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetPrimary(image._id);
                      }}
                      className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] py-0.5 text-center hover:bg-black/70 transition-colors dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                      Set Primary
                    </button>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500 dark:border-gray-500 dark:text-gray-400">
          {t("vehicleProfile.images.noImages")}
        </div>
      )}

      {/* Image Upload Form */}
      {user && (
        <div className="mt-3">
          <button
            onClick={() => setShowImageForm(!showImageForm)}
            className="mx-auto block bg-gradient-to-r from-primary-600 to-primary-700 text-white px-3 py-1.5 rounded-full hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 text-sm dark:from-primary-600 dark:to-primary-700 dark:hover:from-primary-700 dark:hover:to-primary-800 dark:active:from-primary-800 dark:active:to-primary-900"
          >
            {showImageForm
              ? t("vehicleProfile.images.hideForm")
              : t("vehicleProfile.images.addImage")}
          </button>

          {showImageForm && (
            <form onSubmit={onSubmitImage} className="max-w-md mx-auto mt-3">
              <div className="flex flex-col gap-2">
                <div className="text-xs text-gray-500 mb-2">
                  {t("vehicleProfile.images.proTip")}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className="flex-grow border border-gray-300 p-1.5 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:border-gray-700 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder={t("vehicleProfile.images.urlPlaceholder")}
                  />
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-1.5 rounded-r-md hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 font-medium text-sm dark:from-primary-600 dark:to-primary-700 dark:hover:from-primary-700 dark:hover:to-primary-800 dark:active:from-primary-800 dark:active:to-primary-900"
                  >
                    {t("vehicleProfile.images.submit")}
                  </button>
                </div>

                <FileUpload
                  vehicleId={vehicle._id}
                  onUploadComplete={(updatedVehicle) => {
                    setApprovedImages(
                      updatedVehicle.images.filter((img) => img.approvedAt)
                    );
                    setImageMessage(t("vehicleProfile.images.uploadSuccess"));
                    setShowImageForm(false);
                  }}
                  onUploadError={(error) => {
                    console.error("Upload error:", error);
                    setImageMessage(
                      t("vehicleProfile.images.uploadError", { error })
                    );
                  }}
                />
              </div>
            </form>
          )}
        </div>
      )}
      {imageMessage && (
        <p
          className={`mt-2 text-xs text-center dark:text-gray-400 ${
            imageMessage.includes("Error")
              ? "text-red-600 dark:text-red-400"
              : "text-blue-700 dark:text-blue-400"
          }`}
        >
          {imageMessage}
        </p>
      )}
    </div>
  );
};

export default VehicleImages;
